// A job's colour, derived from the job itself.
//
// Ported from Zanetto Tools (phase 7 stage 3). The idea is small and the payoff
// is large: a job keeps ONE colour in every view, on every date range, and no
// matter how its bookings change. Colouring from what happens to be on screen
// is the obvious implementation and it is the wrong one — scroll a week and
// every bar changes colour, which makes the board unreadable precisely when
// someone is trying to follow one job across it.
//
// Dayboard's job numbers are assigned by the server. Whatever their shape, the
// colour comes from the number first and falls back to the id, so it is stable
// across machines, sessions and reloads without storing anything.

export interface JobColour {
  /** Solid fill for a timeline bar. */
  bar: string;
  /**
   * The text colour that goes ON that fill — NOT always white.
   *
   * The ported palette said "every bar is dark enough for white text" and it
   * was not true: measured in a browser on 2026-08-20, ten of the twenty-four
   * failed, some badly (amber-500 with white is 2.15:1, emerald-500 is 2.54:1).
   * A job number nobody can read defeats the point of colouring the bar at all.
   *
   * Rather than darken every bright colour — which would collapse the two runs
   * into one and lose the distinguishability the palette exists for — each
   * entry now names its own text colour, and every pair below clears 4.5:1.
   * Two hues (violet-500 at 4.23:1 white / 4.19:1 dark, indigo-500 at 4.47 /
   * 3.97) failed against BOTH, so those two shifted a shade darker; nothing
   * else moved.
   */
  barText: string;
  /** Tinted background with a matching border, for chips in a grid cell. */
  chip: string;
  /** Just the border colour, for a left stripe. */
  stripe: string;
}

/**
 * Twenty-four slots. The first twelve are the bright run; the second twelve are
 * darker AND rotated half a turn through the hues, so two jobs twelve apart do
 * not land on the light/dark pair of the same colour — they land somewhere else
 * entirely. That rotation is the whole reason there are twenty-four rather than
 * twelve used twice.
 *
 * Each entry names the text colour that belongs on its fill — see `barText`.
 *
 * **There is no orange in here, and that is deliberate (2026-08-31).** The two
 * orange slots became sky when #E07638 became the brand: measured in CIELAB,
 * orange-500 sat ΔE 19.7 from the brand accent, and the closest pair this
 * palette already tolerates INTERNALLY is 14.4 (violet-600 vs indigo-600) — so
 * a job bar was as near the accent as two palette entries are to each other,
 * and on the board it read as a selected state rather than as a job. Sky is
 * ΔE 106 from the accent and still 21.5 from its nearest neighbour (cyan-600),
 * which is better separated than the palette's own accepted minimum.
 *
 * Green measured slightly better on separation and was rejected anyway: green
 * is this product's "done / fine" colour — `banner-ok`, StatusBadge's `good`,
 * the service-due OK chip — and a bar in it invites reading a STATUS into a
 * colour that deliberately carries none. Blue's meaning here is "information",
 * which is not a status a job can be in.
 *
 * Both replacements keep the run's text rule: the bright run takes gray-900
 * (sky-500 is 6.40:1), the dark run takes white (sky-700 is 5.93:1).
 */
const PALETTE: JobColour[] = [
  { bar: 'bg-blue-500', barText: 'text-gray-900', chip: 'bg-blue-100 text-blue-900 border-blue-200', stripe: 'border-l-blue-500' },
  { bar: 'bg-emerald-500', barText: 'text-gray-900', chip: 'bg-emerald-100 text-emerald-900 border-emerald-200', stripe: 'border-l-emerald-500' },
  { bar: 'bg-violet-600', barText: 'text-white', chip: 'bg-violet-100 text-violet-900 border-violet-200', stripe: 'border-l-violet-600' },
  { bar: 'bg-sky-500', barText: 'text-gray-900', chip: 'bg-sky-100 text-sky-900 border-sky-200', stripe: 'border-l-sky-500' },
  { bar: 'bg-rose-500', barText: 'text-gray-900', chip: 'bg-rose-100 text-rose-900 border-rose-200', stripe: 'border-l-rose-500' },
  { bar: 'bg-teal-500', barText: 'text-gray-900', chip: 'bg-teal-100 text-teal-900 border-teal-200', stripe: 'border-l-teal-500' },
  { bar: 'bg-amber-500', barText: 'text-gray-900', chip: 'bg-amber-100 text-amber-900 border-amber-200', stripe: 'border-l-amber-500' },
  { bar: 'bg-indigo-600', barText: 'text-white', chip: 'bg-indigo-100 text-indigo-900 border-indigo-200', stripe: 'border-l-indigo-600' },
  { bar: 'bg-cyan-600', barText: 'text-gray-900', chip: 'bg-cyan-100 text-cyan-900 border-cyan-200', stripe: 'border-l-cyan-600' },
  { bar: 'bg-fuchsia-600', barText: 'text-white', chip: 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200', stripe: 'border-l-fuchsia-600' },
  { bar: 'bg-lime-600', barText: 'text-gray-900', chip: 'bg-lime-100 text-lime-900 border-lime-200', stripe: 'border-l-lime-600' },
  { bar: 'bg-pink-600', barText: 'text-white', chip: 'bg-pink-100 text-pink-900 border-pink-200', stripe: 'border-l-pink-600' },
  // The darker run, rotated half a turn.
  { bar: 'bg-teal-800', barText: 'text-white', chip: 'bg-teal-200 text-teal-900 border-teal-300', stripe: 'border-l-teal-800' },
  { bar: 'bg-amber-700', barText: 'text-white', chip: 'bg-amber-200 text-amber-900 border-amber-300', stripe: 'border-l-amber-700' },
  { bar: 'bg-indigo-800', barText: 'text-white', chip: 'bg-indigo-200 text-indigo-900 border-indigo-300', stripe: 'border-l-indigo-800' },
  { bar: 'bg-cyan-800', barText: 'text-white', chip: 'bg-cyan-200 text-cyan-900 border-cyan-300', stripe: 'border-l-cyan-800' },
  { bar: 'bg-fuchsia-800', barText: 'text-white', chip: 'bg-fuchsia-200 text-fuchsia-900 border-fuchsia-300', stripe: 'border-l-fuchsia-800' },
  { bar: 'bg-lime-800', barText: 'text-white', chip: 'bg-lime-200 text-lime-900 border-lime-300', stripe: 'border-l-lime-800' },
  { bar: 'bg-pink-800', barText: 'text-white', chip: 'bg-pink-200 text-pink-900 border-pink-300', stripe: 'border-l-pink-800' },
  { bar: 'bg-blue-800', barText: 'text-white', chip: 'bg-blue-200 text-blue-900 border-blue-300', stripe: 'border-l-blue-800' },
  { bar: 'bg-emerald-800', barText: 'text-white', chip: 'bg-emerald-200 text-emerald-900 border-emerald-300', stripe: 'border-l-emerald-800' },
  { bar: 'bg-violet-800', barText: 'text-white', chip: 'bg-violet-200 text-violet-900 border-violet-300', stripe: 'border-l-violet-800' },
  { bar: 'bg-sky-700', barText: 'text-white', chip: 'bg-sky-200 text-sky-900 border-sky-300', stripe: 'border-l-sky-700' },
  { bar: 'bg-rose-800', barText: 'text-white', chip: 'bg-rose-200 text-rose-900 border-rose-300', stripe: 'border-l-rose-800' },
];

/**
 * A conflicted booking is never given its job's colour. Red means "this one
 * needs you" everywhere in the product — the same language the notifications
 * feed uses for an acknowledgement you still owe — and a colour that competes
 * with that is worse than no colour.
 */
export const CONFLICT_COLOUR: JobColour = {
  bar: 'bg-red-600',
  barText: 'text-white',
  chip: 'bg-red-100 text-red-900 border-red-300',
  stripe: 'border-l-red-500',
};

/**
 * Maintenance (migration 013): a slate stripe, and deliberately NOT a palette
 * colour.
 *
 * The palette exists so a person can follow one job across a board. Maintenance
 * is not a job — a machine at the mechanic is the absence of work — so giving
 * it a rotating colour would say "this is a job like the others" in the one
 * channel the board uses to mean exactly that.
 *
 * Slate rather than red: it is a planned, ordinary thing. Red is reserved for
 * "this needs you", which is the conflict colour and the language the
 * notifications feed already uses. Nothing in this palette competes with it.
 */
export const MAINTENANCE_COLOUR: JobColour = {
  bar: 'bg-slate-500',
  barText: 'text-white',
  chip: 'bg-slate-100 text-slate-900 border-slate-300',
  stripe: 'border-l-slate-500',
};

/** Cheap stable string hash, for job numbers that do not parse as a sequence. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

/**
 * Pick the palette slot for a job.
 *
 * A trailing run of digits in the job number is treated as a sequence, so
 * consecutively-numbered jobs get consecutive — and therefore different —
 * colours. Anything preceding it is mixed in with a multiplier that shares no
 * factors with the palette size, so the same sequence number under a different
 * prefix (a different year, say) lands somewhere else.
 */
export function paletteIndexFor(jobNumber: string | null | undefined, jobId: string): number {
  const number = String(jobNumber ?? '').trim().toUpperCase();
  const match = /^(.*?)(\d+)$/.exec(number);

  const n = match
    ? hashString(match[1] ?? '') * 7 + Number(match[2])
    : hashString(number !== '' ? number : jobId);

  return ((n % PALETTE.length) + PALETTE.length) % PALETTE.length;
}

/**
 * The colour for one booking's bar or chip.
 *
 * `jobId` is nullable since migration 013: a maintenance booking has no job,
 * and gets the neutral treatment above. A conflict still beats everything —
 * maintenance included, because a flagged maintenance booking is exactly as
 * much of a problem as a flagged job one.
 */
export function jobColour(
  jobNumber: string | null | undefined,
  jobId: string | null,
  inConflict = false
): JobColour {
  if (inConflict) return CONFLICT_COLOUR;
  if (jobId === null) return MAINTENANCE_COLOUR;
  return PALETTE[paletteIndexFor(jobNumber, jobId)]!;
}

export const PALETTE_SIZE = PALETTE.length;
